import { useState, useEffect } from "react";
import { httpService } from "../../../services/httpService";
import {
  FiFileText,
  FiUser,
  FiTag,
  FiClock,
  FiGrid,
  FiCheck,
  FiPlusCircle,
  FiPercent,
  FiInfo,
} from "react-icons/fi";
import { FaIndianRupeeSign } from "react-icons/fa6";

const toNumber = (val) => (isNaN(parseFloat(val)) ? 0 : parseFloat(val));

const ReviewForm = ({ isActive, data, adonServices, onDataChange }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [petTypes, setPetTypes] = useState([]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const getAdonServiceName = (adonId) => {
    const adon = adonServices?.find((a) => a._id === adonId);
    return adon ? adon.name : "";
  };

  const getAdonServicePrice = (adonId) => {
    const adon = adonServices?.find((a) => a._id === adonId);
    return adon ? parseFloat(adon.price) : parseFloat(0);
  };

  const totalAdonPrice = data?.adons?.addons?.reduce((sum, adonId) => {
    return sum + getAdonServicePrice(adonId?._id);
  }, 0);

  const formatPrice = (value) => {
    const num = Number(value);
    return isNaN(num) ? parseFloat(0) : parseFloat(num.toFixed(4)).toString();
  };

  const formatDuration = (service) => {
    if (
      service?.duration &&
      service.duration.flat &&
      service.duration.flat.minutes
    ) {
      const minutes = service.duration.flat.minutes;
      return `${minutes} min`;
    }

    if (service?.flatDurationMinutes) {
      return `${service.flatDurationMinutes} min`;
    }

    if (typeof service?.duration === "number" && service.duration > 0) {
      return `${service.duration} min`;
    }

    return "0 min";
  };

  const fetchCategories = async () => {
    try {
      const { data } = await httpService.get(
        "serviceCategory/getServiceCategories"
      );
      setCategories(data || []);
    } catch (err) {
      console.error("Failed to fetch categories", err);
    }
  };

  const InfoCard = ({ icon: Icon, title, value, subtitle }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-2 hover:shadow-sm transition-all duration-200">
      <div className="flex items-center space-x-2">
        <div className="bg-gray-100 p-1 rounded-md">
          <Icon className="h-3 w-3 text-gray-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-medium text-gray-600 truncate">
            {title}
          </p>
          <p className="text-xs font-semibold text-gray-900 truncate">
            {value}
          </p>
          {subtitle && (
            <p className="text-[10px] text-gray-500 truncate">{subtitle}</p>
          )}
        </div>
      </div>
    </div>
  );

  const PriceCard = ({ title, amount, icon: Icon = FaIndianRupeeSign }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-2 hover:shadow-sm transition-all duration-200">
      <div className="text-center">
        <h4 className="text-[11px] font-medium text-gray-600 mb-0.5">
          {title}
        </h4>
        <div className="flex items-center justify-center space-x-1">
          <Icon className="h-3 w-3 text-gray-700" />
          <span className="text-sm font-bold text-gray-900">{amount}</span>
        </div>
      </div>
    </div>
  );

  const SurchargeGrid = ({ title, options }) => (
    <div className="space-y-1">
      <h4 className="text-[11px] font-semibold text-gray-700 flex items-center">
        <FiTag className="h-2.5 w-2.5 mr-1" />
        {title}
      </h4>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
        {Object.entries(options || {}).map(([key, price]) => (
          <div
            key={key}
            className="border border-gray-200 bg-white rounded p-1 text-center hover:shadow-sm transition-all duration-200"
          >
            <div className="text-[10px] font-medium text-gray-600 uppercase tracking-wide">
              {key}
            </div>
            <div className="flex items-center justify-center space-x-0.5 text-xs font-bold text-gray-800">
              <FaIndianRupeeSign className="h-2 w-2" />
              <span>{price}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const DiscountItem = ({
    label,
    discount,
    discountType,
    icon: Icon = FiCheck,
  }) => (
    <div className="bg-white border border-gray-200 rounded p-1.5 hover:shadow-sm transition-all duration-200">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-1">
          <Icon className="h-2.5 w-2.5 text-gray-600" />
          <span className="text-xs font-medium text-gray-800">{label}</span>
        </div>
        <span className="bg-green-100 text-green-800 text-[12px] font-semibold px-1.5 py-0.5 rounded-full">
          {discount}
          {discountType === "percentage" ? "%" : "₹"} off
        </span>
      </div>
    </div>
  );

  useEffect(() => {
    const fetchPetTypes = async () => {
      try {
        const res = await httpService.get(
          "/vendors/getPetTypes?isEnabled=true"
        );
        const list = res.data?.data ?? res.data ?? [];

        const formatted = list.map((pt) => ({
          id: pt.pet_type_id || pt._id,
          name: pt.name || pt.pet_type_name || "Unknown",
        }));

        setPetTypes(formatted);
      } catch (err) {
        console.error("Failed to fetch pet types", err);
      }
    };

    fetchPetTypes();
  }, []);

  const getPetTypeNames = (petTypeData) => {
    if (!petTypeData || petTypeData.length === 0) {
      return "Not specified";
    }

    const ids = Array.isArray(petTypeData)
      ? petTypeData.map((pt) =>
          typeof pt === "object" ? pt.pet_type_id || pt._id : pt
        )
      : [];

    const names = ids
      .map((id) => petTypes.find((p) => p.id === id)?.name)
      .filter(Boolean);

    return names.length > 0 ? names.join(", ") : "Not specified";
  };

  const renderServiceDetails = () => {
    const { service } = data;
    if (!service) return null;

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gray-100 px-3 py-2 border-b border-gray-200">
          <h3 className="text-sm font-bold  flex items-center">
            <FiFileText className="h-4 w-4 mr-1.5" />
            Service Overview
          </h3>
        </div>

        <div className="p-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <InfoCard icon={FiUser} title="Service Name" value={service.name} />
            <InfoCard icon={FiTag} title="Barcode" value={service.barcode} />
            {/* <InfoCard
              icon={FiFileText}
              title="Category"
              value={service.category?.service_type_name || "Not specified"}
            /> */}
            <InfoCard
              icon={FiGrid}
              title="Pet Type"
              value={getPetTypeNames(service.pet_type)}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderPricingWithDuration = () => {
    const { service } = data;
    if (!service) return null;

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gray-100 px-3 py-2 border-b border-gray-200">
          <h3 className="text-sm font-bold flex items-center">
            <FaIndianRupeeSign className="h-3 w-3 mr-1.5" />
            Pricing & Duration
          </h3>
        </div>

        <div className="p-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <PriceCard
              title="Basic Price"
              amount={service.basicPrice || service.basic_price || "0"}
            />
            <div className="bg-white border border-gray-200 rounded-lg p-2 hover:shadow-sm transition-all duration-200">
              <div className="text-center">
                <h4 className="text-[11px] font-medium text-gray-600">
                  Duration
                </h4>
                <div className="flex items-center justify-center space-x-1 text-gray-700">
                  <FiClock className="h-3 w-3" />
                  <span className="text-sm font-bold">
                    {formatDuration(service)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSurchargesAndDiscounts = () => {
    const { pricingSurcharge } = data;
    if (!pricingSurcharge) return null;

    const hasSurcharges =
      pricingSurcharge.priceBasedOnSize === "yes" ||
      pricingSurcharge.priceBasedOnCoat === "yes";
    const hasDiscounts =
      pricingSurcharge.priceDiscountForMulitpleAppointment === "yes" ||
      pricingSurcharge.priceDiscountForClientLabel === "yes";

    if (!hasSurcharges && !hasDiscounts) return null;

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gray-100 px-3 py-2 border-b border-gray-200">
          <h3 className="text-sm font-bold flex items-center">
            <FiPercent className="h-3 w-3 mr-1.5" />
            Surcharges
          </h3>
        </div>

        <div className="p-3 space-y-2">
          {pricingSurcharge.priceBasedOnSize === "yes" && (
            <SurchargeGrid
              title="Size-based Surcharge"
              options={pricingSurcharge.sizeOptions}
            />
          )}

          {pricingSurcharge.priceBasedOnCoat === "yes" && (
            <SurchargeGrid
              title="Coat-based Surcharge"
              options={pricingSurcharge.coatOptions}
            />
          )}
          {/*
          {pricingSurcharge.priceDiscountForMulitpleAppointment === "yes" && (
            <div className="space-y-1">
              <h4 className="text-[11px] font-semibold flex items-center">
                <FiPlusCircle className="h-2.5 w-2.5 mr-1" />
                Multiple Appointment Discounts
              </h4>
              <div className="space-y-1">
                {pricingSurcharge.multipleAppointmentDiscounts?.map(
                  (discount, index) => (
                    <DiscountItem
                      key={index}
                      label={`${discount.no_of_pet} pets`}
                      discount={discount.discount}
                      discountType={discount.discount_type}
                    />
                  )
                )}
              </div>
            </div>
          )}

          {pricingSurcharge.priceDiscountForClientLabel === "yes" && (
            <div className="space-y-1">
              <h4 className="text-[11px] font-semibold flex items-center">
                <FiInfo className="h-2.5 w-2.5 mr-1" />
                Client Label Discounts
              </h4>
              <div className="space-y-1">
                {pricingSurcharge.clientLabelDiscounts?.map(
                  (discount, index) => (
                    <DiscountItem
                      key={index}
                      label={discount.label}
                      discount={discount.discount}
                      discountType={discount.discount_type}
                    />
                  )
                )}
              </div>
            </div>
          )}
           */}
        </div>
      </div>
    );
  };

  const renderAddons = () => {
    if (!data.adons?.addons?.length) return null;

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gray-100 px-3 py-2 border-b border-gray-200">
          <h3 className="text-sm font-bold flex items-center">
            <FiPlusCircle className="h-3 w-3 mr-1.5" />
            Add-on Services
          </h3>
        </div>

        <div className="p-3">
          <div className="space-y-2">
            {data.adons.addons.map((adon, index) => {
              const adonData = adonServices?.find((a) => a._id === adon._id);
              const duration = adonData?.minutes_duration || 0;
              const price = getAdonServicePrice(adon._id);

              return (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg p-2 hover:shadow-sm transition-all duration-200"
                >
                  <div className="flex justify-between items-center">
                    <p className="text-xs font-semibold text-gray-800">
                      {getAdonServiceName(adon._id)}
                    </p>
                    <div className="flex items-center space-x-2">
                      {duration > 0 && (
                        <div className="flex items-center text-xs text-blue-500">
                          <FiClock className="h-3 w-3 mr-1" />
                          <span>{duration} min</span>
                        </div>
                      )}
                      <div className="flex items-center text-xs font-bold text-red-600">
                        <FaIndianRupeeSign className="h-3 w-3 mr-0.5" />
                        <span>{price}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-3 pt-2 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <span className="text-xs font-semibold text-gray-700">
                Total Add-ons:
              </span>
              <div className="flex items-center text-sm font-bold text-gray-900">
                <FaIndianRupeeSign className="h-3 w-3 mr-1" />
                <span>{formatPrice(totalAdonPrice)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-3 bg-gray-50">
      <div className="max-w-5xl mx-auto space-y-3">
        {renderServiceDetails()}
        {renderPricingWithDuration()}
        {renderSurchargesAndDiscounts()}
        {renderAddons()}
      </div>
    </div>
  );
};

export default ReviewForm;
